import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

export default function FinalMockReviewPage() {
  const { mockId } = useParams();
  const navigate = useNavigate();
  const [scores, setScores] = useState({ reading: null, listening: null, writing: null, speaking: null });
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({});
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) return;

    const fetchScores = async () => {
      try {
        const scoreRef = doc(db, "users", user.uid, "mockScores", mockId);

        const snap = await getDoc(scoreRef);
        if (snap.exists()) {
          setScores(snap.data());
        }

        const feedbackRef = doc(db, "users", user.uid, "mockAnswers", "mock1_finalReview");
        const feedbackSnap = await getDoc(feedbackRef);
        if (feedbackSnap.exists()) {
          const data = feedbackSnap.data();
          setFeedback(data);

          // Merge writing and speaking scores from feedback into main scores object
          setScores(prev => ({
            ...prev,
            writing: typeof data.writingScore === 'number' ? data.writingScore : prev.writing,
            speaking: typeof data.speakingScore === 'number' ? data.speakingScore : prev.speaking
          }));
        }
      } catch (err) {
        console.error("Error fetching mock scores or feedback:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [mockId, user]);

  if (loading) return <p className="p-4">Loading your final results...</p>;

  const total = [scores.reading, scores.listening, scores.writing, scores.speaking]
    .filter(n => typeof n === "number")
    .reduce((a, b) => a + b, 0);

  return (
    <div className="p-8 max-w-2xl mx-auto text-center">
      <h1 className="text-3xl font-bold mb-6 text-darkblue-800"><p><span role="img" aria-label="party popper">🎉</span> You've completed...</p>
      Final Mock Exam Results</h1>

      <div className="bg-gray-100 rounded-xl shadow p-6 space-y-4 text-left">
        <div>
          <strong>Reading:</strong>{" "}
          {scores.reading != null ? `${scores.reading} / 30` : <span className="text-gray-500">Pending</span>}
        </div>
        <div>
          <strong>Listening:</strong>{" "}
          {scores.listening != null ? `${scores.listening} / 30` : <span className="text-gray-500">Pending</span>}
        </div>
        <div>
          <strong>Writing:</strong>{" "}
          {scores.writing != null ? `${scores.writing} / 30` : <span className="text-gray-500">Pending (teacher review)</span>}
        </div>
        <div>
          <strong>Speaking:</strong>{" "}
          {scores.speaking != null ? `${scores.speaking} / 30` : <span className="text-gray-500">Pending (teacher review)</span>}
        </div>
      </div>

      <div className="mt-6 text-xl font-semibold">
        Total Score:{" "}
        <span className="text-purple-700 text-4xl font-bold">
          {total} / 120
        </span>
      </div>

      {feedback && (feedback.feedbackWriting1 || feedback.feedbackWriting2 || feedback.feedbackSpeaking) && (
        <div className="mt-10 text-left bg-white shadow rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4"><span role="img" aria-label="memo">📝</span>
          Teacher Feedback</h2>

          {feedback.feedbackWriting1 && (
            <div className="mb-4">
              <h3 className="font-semibold">Writing Task 1:</h3>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: feedback.feedbackWriting1 }} />
            </div>
          )}

          {feedback.feedbackWriting2 && (
            <div className="mb-4">
              <h3 className="font-semibold">Writing Task 2:</h3>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: feedback.feedbackWriting2 }} />
            </div>
          )}

          {feedback.feedbackSpeaking && (
            <div className="mb-4">
              <h3 className="font-semibold">Speaking:</h3>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: feedback.feedbackSpeaking }} />
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => navigate('/')}
        className="mt-8 px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-lg shadow"
      >
        Back to Dashboard
      </button>
    </div>
  );
}
